import React, { useRef, useState, useEffect } from "react";
import { Box, useMediaQuery } from "@mui/material";
import rawStyles from "../../styles";
import mapImg from "../../../../assets/images/map.png";
import Statistis from "./Statistics";
import ImageCarousel from "./ImageCarousel";
import MapsView from "./MapsView";
import { useTheme } from "@emotion/react";

const formatKey = (key) =>
  key
    .split("_")
    .map((w) =>
      w.toUpperCase() === w ? w : w.charAt(0).toUpperCase() + w.slice(1),
    )
    .join(" ")
    .replace(/\bHvac\b/, "HVAC");

const scopeItemPillStyle = (value) => {
  const v = String(value).toLowerCase().trim();
  if (v === "no" || v === "none")
    return { background: "#FEF2F2", keyColor: "#DC2626", valColor: "#DC2626" };
  if (v === "yes" || v === "major" || v === "minor")
    return { background: "#F0FDF4", keyColor: "#16A34A", valColor: "#16A34A" };
  return { background: "#F3F4F6", keyColor: "#0D0D0E", valColor: "#0D0D0E" };
};

const chipBase = {
  fontSize: "12px",
  padding: "4px 12px",
  borderRadius: "9999px",
  display: "inline-block",
};

export default function ProjectOverview({ details }) {
  const [showLargeMap, setShowLargeMap] = useState(false);
  const largeMapRef = useRef(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const styles = rawStyles(isMobile);

  useEffect(() => {
    if (showLargeMap && largeMapRef.current) {
      largeMapRef.current.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }
  }, [showLargeMap]);

  const smallMapContainerStyle = {
    width: isMobile ? "30vw" : "200px",
    height: isMobile ? "80px" : "120px",
    borderRadius: "12px",
    pointerEvents: "none",
  };

  const largeMapContainerStyle = {
    width: "100%",
    height: "35vh",
    borderRadius: "12px",
    marginTop: "10px",
  };

  const toggleLargeMap = () => {
    setShowLargeMap((prev) => {
      if (!prev) {
        setTimeout(() => {
          largeMapRef.current?.scrollIntoView({
            behavior: "smooth",
            block: "nearest",
          });
        }, 50);
      }
      return !prev;
    });
  };

  const formJson = details?.form_json || {};
  const scopeInclusions =
    Array.isArray(formJson.scope_inclusions) &&
    formJson.scope_inclusions.length > 0
      ? formJson.scope_inclusions
      : null;
  const scopeItems =
    formJson.scope_items &&
    typeof formJson.scope_items === "object" &&
    Object.keys(formJson.scope_items).length > 0
      ? formJson.scope_items
      : null;

  return (
    <>
      <div style={{ ...styles.overview_section, isMobile }}>
        <div style={{ ...styles.section_1, ...styles.columnFlex }}>
          <ImageCarousel details={details} />
          <Statistis details={details} />

          <div style={styles.infoGrid}>
            <div style={{ ...styles.columnFlex, gap: "5px" }}>
              <h3 style={styles.section_heading}>Customer Type</h3>
              <div style={styles.section_sub_heading}>
                {details?.client_type || "-"}
              </div>
            </div>
            <div style={{ ...styles.columnFlex, gap: "5px" }}>
              <h3 style={styles.section_heading}>Legal Entity</h3>
              <div style={styles.section_sub_heading}>
                {details?.unit_type || "-"}
              </div>
            </div>
          </div>

          {scopeInclusions && (
            <div style={{ ...styles.columnFlex, marginTop: "2vh", gap: "8px" }}>
              <h3 style={styles.section_heading}>Scope Inclusions</h3>
              <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
                {scopeInclusions.map((item) => (
                  <span
                    key={item}
                    style={{
                      ...chipBase,
                      background: "#F3F4F6",
                      color: "#0D0D0E",
                    }}
                  >
                    {item}
                  </span>
                ))}
              </div>
            </div>
          )}

          {scopeItems && (
            <div style={{ ...styles.columnFlex, marginTop: "2vh", gap: "8px" }}>
              <h3 style={styles.section_heading}>Scope Items</h3>
              <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
                {Object.entries(scopeItems).map(([key, value]) => {
                  const { background, keyColor, valColor } =
                    scopeItemPillStyle(value);
                  return (
                    <span key={key} style={{ ...chipBase, background }}>
                      <span style={{ fontWeight: 500, color: keyColor }}>
                        {formatKey(key)}:
                      </span>{" "}
                      <span style={{ color: valColor }}>
                        {formatKey(String(value))}
                      </span>
                    </span>
                  );
                })}
              </div>
            </div>
          )}

          <div style={{ ...styles.columnFlex, marginTop: "1vh" }}>
            <div style={styles.section_heading}>Description</div>
            <h3 style={styles.section_sub_heading}>{details?.description}</h3>
          </div>

          <div style={{ ...styles.columnFlex, marginTop: "1vh" }}>
            <div style={styles.section_heading}>Scope of work</div>
            <h3 style={styles.section_sub_heading}>
              {details?.scope_of_work || "-"}
            </h3>
          </div>
        </div>

        {isMobile && (
          <div
            style={{
              borderTop: "1px solid #D7D8D9",
              width: "100%",
              margin: "16px 0",
            }}
          />
        )}

        <div style={{ ...styles.section_2, ...styles.columnFlex, gap: "20px" }}>
          <div style={styles.section_heading}>Members</div>
          <div style={styles.columnFlex}>
            <h3 style={styles.section_sub_heading}>Customer</h3>
            <Box display="flex" alignItems="center">
              {details?.user_data && (
                <Box
                  component="img"
                  src={
                    details?.user_data?.profile_url ||
                    "https://renohome.blob.core.windows.net/reno-cms/e56d3d53-e335-425f-990e-16e6b2bbee1b"
                  }
                  alt="Customer Icon"
                  sx={{
                    width: 35,
                    height: 35,
                    borderRadius: "50%",
                    boxShadow: "0px 4px 2.67px 0px #00000026",
                    marginRight: 1,
                  }}
                />
              )}
              {details?.user_data?.username}
            </Box>
          </div>

          <div style={styles.columnFlex}>
            <h3 style={styles.section_sub_heading}>Contractor</h3>
            <Box display="flex" alignItems="center">
              {details?.Contractor && (
                <Box
                  component="img"
                  src={
                    details?.Contractor?.profile_url ||
                    "https://renohome.blob.core.windows.net/reno-cms/e56d3d53-e335-425f-990e-16e6b2bbee1b"
                  }
                  alt="Contractor Icon"
                  sx={{
                    width: 35,
                    height: 35,
                    borderRadius: "50%",
                    boxShadow: "0px 4px 2.67px 0px #00000026",
                    marginRight: 1,
                  }}
                />
              )}
              {details?.Contractor?.company_name}
            </Box>
          </div>

          <div style={{ ...styles.columnFlex, marginTop: "2vh" }}>
            <div style={styles.section_heading}>Location</div>
            <div
              style={{
                ...styles.location,
                ...styles.rowFlex,
                gap: "10px",
                justifyContent: "space-between",
              }}
            >
              <p style={{ fontSize: isMobile ? "12px" : "14px" }}>
                {details?.location || "N/A"}
              </p>
              {details?.lat !== null && details?.lng !== null ? (
                <div onClick={toggleLargeMap} style={{ cursor: "pointer" }}>
                  <div style={{ pointerEvents: "none" }}>
                    <MapsView
                      details={details}
                      mapContainerStyle={smallMapContainerStyle}
                    />
                  </div>
                </div>
              ) : (
                <Box
                  component="img"
                  sx={{
                    height: isMobile ? 150 : 75,
                    width: isMobile ? "80vw" : 120,
                  }}
                  src={mapImg}
                />
              )}
            </div>
            {showLargeMap && (
              <div ref={largeMapRef} style={{ width: "100%" }}>
                <MapsView
                  details={details}
                  mapContainerStyle={largeMapContainerStyle}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
